import { ApiRequestError, type SavedConfig } from "@cfg/api-contract";
import { compareSemver } from "@cfg/design-sdk";
import {
  ConfiguratorProvider,
  FitTagInput,
  IssueList,
  ParamForm,
  PresetBar,
  Viewport,
  WidgetRegistryProvider,
  buildConfiguratorSearch,
  getDraft,
  parseConfiguratorUrl,
  saveDraft,
  useDesign,
  useExport,
  useMesh,
  useMigrate,
  useParams,
  type Draft,
  type FitTagValue,
} from "@cfg/configurator-ui";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams as useRouteParams } from "react-router";
import { useApp } from "../app-context.tsx";
import { useAuthUser } from "../auth.ts";
import { getKernelClient } from "../kernel.ts";
import { DefaultConfiguratorFrame, DefaultEmptyState } from "./defaults.tsx";
import { DisplayNameForm } from "./DisplayNameForm.tsx";

interface Opened {
  designId: string;
  version: string;
  bundleUrl: string;
  latest: string;
  deprecated: boolean;
  params: Record<string, unknown>;
  /** Saved config (server) or draft (local) this was opened from. */
  saved?: { kind: "config"; config: SavedConfig } | { kind: "draft"; draft: Draft };
}

export function ConfiguratorPage() {
  const { id = "" } = useRouteParams();
  const location = useLocation();
  const { registry, api, brand } = useApp();
  const Empty = brand.slots?.EmptyState ?? DefaultEmptyState;
  const urlState = useMemo(() => parseConfiguratorUrl(location.search), [location.search]);

  const opened = useQuery({
    queryKey: ["open", id, urlState.version, urlState.configId],
    queryFn: async (): Promise<Opened | null> => {
      let params = urlState.params;
      let version = urlState.version;
      let saved: Opened["saved"];
      if (urlState.configId) {
        const draft = await getDraft(urlState.configId);
        if (draft) {
          saved = { kind: "draft", draft };
          params = draft.params;
          version = draft.version;
        } else if (api) {
          const { config } = await api.getConfig(urlState.configId);
          saved = { kind: "config", config };
          params = config.params;
          version = config.version;
        }
      }
      const r = await registry.resolve(id, version);
      if (!r) return null;
      // Params handed over by the "update to <version>" flow.
      const handoverKey = `cfg-migrated:${id}@${r.version}`;
      const handover = sessionStorage.getItem(handoverKey);
      if (handover) {
        sessionStorage.removeItem(handoverKey);
        try {
          params = JSON.parse(handover) as Record<string, unknown>;
        } catch {
          /* ignore */
        }
      }
      return { designId: id, version: r.version, bundleUrl: r.bundleUrl, latest: r.latest, deprecated: r.deprecated, params, saved };
    },
  });

  if (opened.isLoading) return <p className="muted page">Loading design…</p>;
  if (opened.isError) return <Empty title="Could not open this design" body={String(opened.error)} />;
  if (!opened.data) return <Empty title="Design not found" body={`${id}${urlState.version ? `@${urlState.version}` : ""} is not in this catalog.`} />;
  return <ConfiguratorScreen key={`${opened.data.designId}@${opened.data.version}:${urlState.configId ?? ""}`} opened={opened.data} />;
}

function ConfiguratorScreen({ opened }: { opened: Opened }) {
  const { brand, copy, analytics } = useApp();
  const navigate = useNavigate();
  const client = getKernelClient();
  const key = `${opened.designId}@${opened.version}`;
  const defaultsRef = useRef<Record<string, unknown>>({});

  useEffect(() => analytics.track("view", opened.designId, opened.version), [analytics, opened.designId, opened.version]);

  const onParamsChange = useCallback(
    (params: Record<string, unknown>) => {
      const search = buildConfiguratorSearch(
        { designId: undefined, version: opened.version, params, configId: opened.saved ? (opened.saved.kind === "draft" ? opened.saved.draft.id : opened.saved.config.id) : undefined },
        defaultsRef.current,
      );
      if (search !== window.location.search) window.history.replaceState(null, "", `${window.location.pathname}${search}`);
      analytics.track("configure", opened.designId, opened.version);
    },
    [opened, analytics],
  );

  return (
    <ConfiguratorProvider client={client} designKey={key} bundleUrl={opened.bundleUrl} initialParams={opened.params} onParamsChange={onParamsChange}>
      <WidgetRegistryProvider widgets={brand.widgets}>
        <Screen opened={opened} defaultsRef={defaultsRef} onOpenVersion={(v) => navigate(`/design/${encodeURIComponent(opened.designId)}?v=${v}`)} />
      </WidgetRegistryProvider>
    </ConfiguratorProvider>
  );
}

function Screen({ opened, defaultsRef, onOpenVersion }: { opened: Opened; defaultsRef: React.MutableRefObject<Record<string, unknown>>; onOpenVersion: (v: string) => void }) {
  const { brand, copy, api, auth, analytics } = useApp();
  const user = useAuthUser(auth);
  const Frame = brand.slots?.ConfiguratorFrame ?? DefaultConfiguratorFrame;
  const { description, status, error } = useDesign();
  const { params, reset, dirty } = useParams();
  const { stats, issues } = useMesh();
  const exporter = useExport((info) => analytics.track("export", opened.designId, opened.version, { format: info.format, bytes: info.bytes }));
  const migrate = useMigrate();
  const scene = brand.scenes[brand.config.scene];
  const [saveState, setSaveState] = useState<{ busy: boolean; msg: string | null; id?: string; kind?: "draft" | "config" }>({
    busy: false,
    msg: null,
    id: opened.saved ? (opened.saved.kind === "draft" ? opened.saved.draft.id : opened.saved.config.id) : undefined,
    kind: opened.saved?.kind,
  });
  const [name, setName] = useState(opened.saved ? (opened.saved.kind === "draft" ? opened.saved.draft.name : opened.saved.config.name) : "");
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const savedFields = opened.saved ? (opened.saved.kind === "draft" ? opened.saved.draft : opened.saved.config) : undefined;
  const [fits, setFits] = useState<FitTagValue[]>(savedFields?.fits ?? []);
  const [printed, setPrinted] = useState(savedFields?.printed ?? false);
  const [notes, setNotes] = useState(savedFields?.notes ?? "");
  const [visibility, setVisibility] = useState<"private" | "public">(opened.saved?.kind === "config" ? opened.saved.config.visibility : "private");
  const [publish, setPublish] = useState<{ busy: boolean; msg: string | null; needsName: boolean }>({ busy: false, msg: null, needsName: false });
  const features = brand.config.features;
  const fitConfig = brand.config.fitTags;
  useEffect(() => {
    if (description) defaultsRef.current = description.defaults;
  }, [description, defaultsRef]);

  const newer = compareSemver(opened.latest, opened.version) > 0 ? opened.latest : null;

  const save = async () => {
    if (!description) return;
    setSaveState((s) => ({ ...s, busy: true, msg: null }));
    const title = name.trim() || `${String(description.meta.title ?? description.id)} · ${new Date().toLocaleDateString()}`;
    // An empty notes string clears them on update.
    const tagged = features.fitTags ? { fits, printed, notes: notes.trim() } : {};
    try {
      if (user && api) {
        const cfg =
          saveState.kind === "config" && saveState.id
            ? await api.updateConfig(saveState.id, { name: title, params, version: opened.version, ...tagged })
            : await api.createConfig({ name: title, designId: opened.designId, version: opened.version, params, ...tagged });
        setVisibility(cfg.visibility);
        setSaveState({ busy: false, msg: copy("configurator.saved"), id: cfg.id, kind: "config" });
        window.history.replaceState(null, "", `${window.location.pathname}${buildConfiguratorSearch({ version: opened.version, params, configId: cfg.id }, defaultsRef.current)}`);
      } else {
        const draft = await saveDraft({ id: saveState.kind === "draft" ? saveState.id : undefined, name: title, designId: opened.designId, version: opened.version, params, brand: brand.config.id, ...tagged });
        setSaveState({ busy: false, msg: auth.enabled ? `${copy("configurator.saved")} · ${copy("auth.required")}` : copy("configurator.saved"), id: draft.id, kind: "draft" });
        window.history.replaceState(null, "", `${window.location.pathname}${buildConfiguratorSearch({ version: opened.version, params, configId: draft.id }, defaultsRef.current)}`);
      }
      setName(title);
      analytics.track("save", opened.designId, opened.version);
    } catch (err) {
      setSaveState((s) => ({ ...s, busy: false, msg: (err as Error).message }));
    }
  };

  /** Publish the last saved state (or make it private again). Prompts for a display name the first time. */
  const togglePublish = async () => {
    if (!api || saveState.kind !== "config" || !saveState.id) return;
    setPublish({ busy: true, msg: null, needsName: false });
    try {
      const cfg = visibility === "public" ? await api.unpublishConfig(saveState.id) : await api.publishConfig(saveState.id);
      setVisibility(cfg.visibility);
      setPublish({ busy: false, msg: null, needsName: false });
    } catch (err) {
      const needsName = err instanceof ApiRequestError && err.body?.code === "display_name_required";
      setPublish({ busy: false, msg: needsName ? null : (err as Error).message, needsName });
    }
  };

  const share = async () => {
    if (api && user && saveState.kind === "config" && saveState.id) {
      const r = await api.shareConfig(saveState.id);
      setShareUrl(`${window.location.origin}/share/${r.token}`);
    } else {
      setShareUrl(window.location.href);
    }
    analytics.track("share", opened.designId, opened.version);
    try {
      await navigator.clipboard.writeText(shareUrl ?? window.location.href);
    } catch {
      /* clipboard may be unavailable */
    }
  };

  const upgrade = async () => {
    if (!newer) return;
    // Migrate the current params in the worker (design.migrateFrom), then open the
    // newer version with them; saving afterwards writes a new revision, never in place.
    const client = getKernelClient();
    const target = await registryResolve(newer);
    if (target) await client.load(`${opened.designId}@${newer}`, target.bundleUrl);
    const migrated = await client.migrate(`${opened.designId}@${newer}`, opened.version, params).catch(() => params);
    sessionStorage.setItem(`cfg-migrated:${opened.designId}@${newer}`, JSON.stringify(migrated));
    onOpenVersion(newer);
  };
  const { registry } = useApp();
  const registryResolve = (v: string) => registry.resolve(opened.designId, v);
  void migrate;

  const title = description ? String(description.meta.title ?? description.id) : opened.designId;
  const printNotes = description ? (description.meta.printNotes as string | undefined) : undefined;

  return (
    <section className="page page-configurator">
      <header className="page-head configurator-head">
        <div>
          <h1>{title}</h1>
          {description?.meta.description ? <p className="page-sub">{String(description.meta.description)}</p> : null}
        </div>
        <div className="configurator-meta">
          <span className="version-pill" title="Design version pinned for this configuration">
            v{opened.version}
          </span>
          {opened.deprecated && <span className="version-pill is-warn">deprecated</span>}
          {newer && brand.config.features.saveConfigs && (
            <button type="button" className="cfg-btn" onClick={upgrade}>
              {copy("configurator.update", { version: newer })}
            </button>
          )}
        </div>
      </header>

      <Frame
        aside={
          printNotes ? (
            <div className="print-notes">
              <strong>Print notes.</strong> {printNotes}
            </div>
          ) : undefined
        }
      >
        <div className="configurator-layout">
          <div className="configurator-stage">
            <Viewport preset={scene} modelColor={brand.config.theme.colors.model} showLabels={brand.config.features.floatingLabels} />
            <div className="stage-stats">
              {stats ? (
                <>
                  <span>{(stats.bbox.max[0] - stats.bbox.min[0]).toFixed(1)} × {(stats.bbox.max[1] - stats.bbox.min[1]).toFixed(1)} × {(stats.bbox.max[2] - stats.bbox.min[2]).toFixed(1)} mm</span>
                  <span>{(stats.volume / 1000).toFixed(1)} cm³</span>
                  <span>{stats.numTri.toLocaleString()} tris · {stats.ms.toFixed(0)} ms</span>
                </>
              ) : (
                <span>{status === "error" ? error : copy("configurator.building")}</span>
              )}
            </div>
          </div>

          <aside className="configurator-panel">
            <div className="panel-title-row">
              <h2>{copy("configurator.params")}</h2>
              <button type="button" className="cfg-chip" onClick={reset} disabled={!dirty && !opened.saved}>
                {copy("configurator.reset")}
              </button>
            </div>
            <PresetBar label={copy("configurator.presets")} />
            <ParamForm />
            <IssueList title={copy("configurator.warnings")} />

            <div className="panel-actions">
              {brand.config.features.exportStl && (
                <button type="button" className="cfg-btn cfg-btn-primary cfg-btn-block" disabled={!!exporter.busy || status !== "ready" || issues.some((i) => i.level === "error")} onClick={exporter.exportStl}>
                  {exporter.busy === "stl" ? "Exporting…" : copy("configurator.export.stl")}
                </button>
              )}
              {brand.config.features.export3mf && (
                <button type="button" className="cfg-btn cfg-btn-block" disabled={!!exporter.busy || status !== "ready" || issues.some((i) => i.level === "error")} onClick={exporter.export3mf}>
                  {exporter.busy === "3mf" ? "Exporting…" : copy("configurator.export.3mf")}
                </button>
              )}
              {exporter.error && <p className="cfg-error">{exporter.error}</p>}
              {exporter.last && <p className="muted small">{exporter.last.filename} · {(exporter.last.bytes / 1024).toFixed(0)} KB</p>}
            </div>

            {brand.config.features.saveConfigs && (
              <div className="panel-save">
                <input className="cfg-input" placeholder="Name this configuration" value={name} onChange={(e) => setName(e.target.value)} />
                {features.fitTags && (
                  <>
                    <FitTagInput
                      value={fits}
                      onChange={setFits}
                      label={fitConfig.label}
                      pattern={fitConfig.refPattern}
                      refPlaceholder={fitConfig.refPlaceholder}
                      namePlaceholder={fitConfig.namePlaceholder}
                      addLabel={copy("fits.add")}
                      messages={{ invalid: copy("fits.invalid"), max: copy("fits.max", { max: 10 }) }}
                    />
                    <label className="cfg-check">
                      <input type="checkbox" checked={printed} onChange={(e) => setPrinted(e.target.checked)} /> {copy("fits.printed")}
                    </label>
                    <textarea className="cfg-input cfg-textarea" rows={2} maxLength={1000} placeholder={copy("fits.notes")} value={notes} onChange={(e) => setNotes(e.target.value)} />
                  </>
                )}
                <div className="panel-save-row">
                  <button type="button" className="cfg-btn" disabled={saveState.busy || status !== "ready"} onClick={save}>
                    {saveState.busy ? "Saving…" : copy("configurator.save")}
                  </button>
                  {brand.config.features.shareLinks && (
                    <button type="button" className="cfg-btn" onClick={share} disabled={status !== "ready"}>
                      {copy("configurator.share")}
                    </button>
                  )}
                </div>
                {saveState.msg && <p className="muted small">{saveState.msg}</p>}
                {features.publicGallery && user && api && (
                  <div className="panel-publish">
                    {saveState.kind === "config" && saveState.id ? (
                      <div className="panel-save-row">
                        <button type="button" className="cfg-btn" disabled={publish.busy} onClick={togglePublish}>
                          {visibility === "public" ? copy("community.unpublish") : copy("community.publish")}
                        </button>
                        {visibility === "public" && (
                          <a className="version-pill" href={`/community/${encodeURIComponent(saveState.id)}`}>
                            {copy("community.public")}
                          </a>
                        )}
                      </div>
                    ) : (
                      <p className="muted small">{copy("community.publishHint")}</p>
                    )}
                    {publish.needsName && <DisplayNameForm onDone={() => void togglePublish()} />}
                    {publish.msg && <p className="cfg-error">{publish.msg}</p>}
                  </div>
                )}
                {shareUrl && (
                  <p className="muted small share-url">
                    <a href={shareUrl}>{shareUrl}</a> (copied)
                  </p>
                )}
                {!user && auth.enabled && (
                  <p className="muted small">
                    {copy("auth.required")}{" "}
                    <button type="button" className="link" onClick={() => void auth.signIn()}>
                      {copy("auth.signIn")}
                    </button>
                  </p>
                )}
              </div>
            )}
          </aside>
        </div>
      </Frame>
    </section>
  );
}
